"use client";

import React from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Header from "@/components/layout/header";
import HeroSection from "@/components/landing/HeroSection";
import TrustBadgesSection from "@/components/landing/TrustBadgesSection";
import FeatureSection from "@/components/landing/FeatureSection";
import Footer from "@/components/layout/footer";
import FaqSection from "@/components/landing/FaqSection";
import ReviewSection from "@/components/landing/ReviewSection";
import ProductSection from "@/components/landing/ProductSection";

const audience = [
  {
    title: "Manufacturers",
    desc: "Reach verified distributors and expand your market presence across Nigeria without the guesswork.",
    href: "/for-manufacturers",
    image: "/assets/img/manufacturers-bg.jpg",
    cta: "Get started as a manufacturer",
  },
  {
    title: "Distributors",
    desc: "Manage your product catalogue, respond to quote requests, and connect with buyers at scale.",
    href: "/for-distributors",
    image: "/assets/img/distributor-bg.jpg",
    cta: "Start distributing today",
  },
  {
    title: "Homeowners",
    desc: "Compare trusted solar providers, request quotes, and power your home without overpaying.",
    href: "/for-consumers",
    image: "/assets/img/homeowners-bg.jpg",
    cta: "Find solar for your home",
  },
  {
    title: "Recyclers",
    desc: "List your services, accept recycling requests, and contribute to a cleaner energy ecosystem.",
    href: "/for-recyclers",
    image: "/assets/img/recycler-bg.jpg",
    cta: "Join as a recycler",
  },
];

const solarSystems = [
  {
    title: "Solar systems for remote workers and business owners",
    desc: "Discover the ideal solar solution for your home or business from our list of affordable solar bundles.",
    cta: "Shop solar system",
    href: "/marketplace?category=solar+kits",
    image: "/assets/img/solar-remote.jpg",
  },
  {
    title: "Get solar products in bulk to stock up your shop",
    desc: "Equip your store with our quality solar products, and get to pay in installments.",
    cta: "Explore solar products",
    href: "/marketplace",
    image: "/assets/img/solar-business.jpg",
  },
  {
    title: "Affordable solar bundles designed for students",
    desc: "Stay powered through lectures, projects, and study nights with compact, budget-friendly solar kits made for student life.",
    cta: "Shop student bundles",
    href: "/marketplace?category=solar+kits",
    image: "/assets/img/solar-student.jpg",
  },
  {
    title: "Power uninterrupted learning in schools",
    desc: "Keep classrooms bright and devices charged throughout the school day. Reliable solar solutions designed for Nigerian primary and secondary schools.",
    cta: "Explore solutions",
    href: "/marketplace?category=solar+kits",
    image: "/assets/img/solar-school.jpg",
  },
];

export default function Home() {
  return (
    <div>
      <Header />

      {/* HERO SECTION */}
      <HeroSection />

      {/* WHO IT IS FOR */}
      <section className="bg-[#f4f6f6] py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-sm font-medium text-[#17BC2E] uppercase tracking-widest mb-3"
            >
              One platform, every role
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-[#080A09] text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight"
            >
              Built for the Entire Solar Ecosystem
            </motion.h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {audience.map(({ title, desc, href, image, cta }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="group relative h-80 rounded-2xl overflow-hidden"
              >
                <img
                  src={image}
                  alt={title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                  <h3 className="font-bold text-lg leading-snug mb-1.5">{title}</h3>
                  <p className="text-white/70 text-xs leading-relaxed mb-3">{desc}</p>
                  <Link
                    href={href}
                    className="inline-flex items-center gap-1.5 bg-[#FFB600] text-[#002301] text-xs font-semibold px-4 py-1.5 rounded-full hover:bg-white hover:text-[#002301] transition-all duration-300"
                  >
                    {cta} <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SOLAR PRODUCT CATEGORIES */}
      <section className="bg-[#f4f6f6] py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-sm font-medium text-[#17BC2E] uppercase tracking-widest mb-3"
            >
              Browse the Marketplace
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-[#080A09] text-3xl sm:text-4xl font-bold leading-tight"
            >
              Shop by Category
            </motion.h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { name: "Solar Panels", image: "/assets/img/solar-panels.png", href: "/marketplace?category=solar+panels" },
              { name: "Batteries", image: "/assets/img/batteries.png", href: "/marketplace?category=batteries" },
              { name: "Inverters", image: "/assets/img/inverters.png", href: "/marketplace?category=inverters" },
              { name: "Solar Kits", image: "/assets/img/solar-bundles.png", href: "/marketplace?category=solar+kits" },
            ].map(({ name, image, href }, i) => (
              <motion.div
                key={name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.4 }}
              >
                <Link href={href} className="group block relative overflow-hidden rounded-2xl bg-gray-100 aspect-square">
                  <img
                    src={image}
                    alt={name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#002301]/80 via-[#002301]/10 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="text-white font-bold text-sm sm:text-base leading-snug">{name}</p>
                    <p className="text-white/60 text-xs mt-0.5">Shop now →</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES SECTION */}
      <FeatureSection />

      {/* PRODUCTS SECTION */}
      <ProductSection />

      {/* SOLAR SYSTEMS 4-CARD SECTION */}
      <section className="bg-white py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-sm font-medium text-[#17BC2E] uppercase tracking-widest mb-3"
            >
              Solar for every lifestyle
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-[#080A09] text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight"
            >
              Find the Right Solar System for You
            </motion.h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {solarSystems.map(({ title, desc, cta, href, image }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="group relative h-96 rounded-2xl overflow-hidden"
              >
                <img
                  src={image}
                  alt={title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = "/assets/img/fallback.jpg";
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="font-bold text-lg leading-snug mb-1.5">{title}</h3>
                  <p className="text-white/70 text-sm leading-relaxed mb-4">{desc}</p>
                  <Link
                    href={href}
                    className="inline-flex items-center gap-2 bg-[#17BC2E] text-white text-sm font-semibold px-5 py-2 rounded-full hover:bg-[#14a827] transition-colors duration-300"
                  >
                    {cta} <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS SECTION */}
      <ReviewSection />

      {/* SOLAR EXPERT CTA (green card) */}
      <section className="bg-white py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-[#17BC2E] rounded-2xl px-8 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6"
          >
            <div className="flex-1">
              <h2 className="text-black font-bold text-2xl sm:text-3xl leading-tight mb-2">
                Don't know the right solar product to buy?
              </h2>
              <p className="text-black/70 text-sm">Speak to our solar experts for guidance.</p>
            </div>
            <Link
              href="/consultation"
              className="flex-shrink-0 bg-[#002301] text-white px-6 py-3 rounded-xl font-semibold text-sm hover:bg-black transition-colors duration-200"
            >
              Speak to a solar expert
            </Link>
          </motion.div>
        </div>
      </section>

      {/* FAQS Section */}
      <FaqSection />

      {/* SOLAR CALCULATOR SECTION */}
      <section className="bg-white py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative rounded-2xl overflow-hidden shadow-xl"
            >
              <img
                src="/assets/img/solar-calculator-hero.png"
                alt="Solar Calculator"
                className="w-full object-cover aspect-[4/3]"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-[#002301]/20 to-transparent pointer-events-none" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
            >
              <p className="text-sm font-medium text-[#17BC2E] uppercase tracking-widest mb-3">Free Tool</p>
              <h2 className="text-[#080A09] text-3xl sm:text-4xl font-bold leading-tight mb-4">
                Calculate Your Solar Savings
              </h2>
              <p className="text-gray-500 text-base leading-relaxed mb-8">
                Find out how much solar power you need and what it will cost. Our free calculator estimates system size, total cost, and payback period, based on your actual energy usage and location in Nigeria.
              </p>
              <Link
                href="/solar-calculator"
                className="inline-flex items-center gap-2 bg-[#002301] text-white px-7 py-3.5 rounded-full font-semibold text-sm hover:bg-black hover:scale-105 transition-all duration-300"
              >
                Try Solar Calculator
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FINANCING SECTION */}
      <section className="bg-[#002301] py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-10">
          <svg width="100%" height="100%">
            <defs><pattern id="fin-home-dots" width="30" height="30" patternUnits="userSpaceOnUse"><circle cx="1.5" cy="1.5" r="1.5" fill="#17BC2E" /></pattern></defs>
            <rect width="100%" height="100%" fill="url(#fin-home-dots)" />
          </svg>
        </div>
        <div className="absolute right-0 top-0 w-72 h-72 bg-[#FFB600]/8 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-5">
              <span className="text-white/80 text-xs font-medium">Solar Financing</span>
            </span>
            <h2 className="text-white font-bold text-3xl sm:text-4xl leading-tight mb-4">
              Own Solar Today.<br />
              <span className="text-[#FFB600]">Pay Over Time.</span>
            </h2>
            <p className="text-white/60 text-base leading-relaxed mb-8">
              Flexible financing plans make clean solar energy accessible to every Nigerian home and business. Zero upfront cost options available, repay over 24 months.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link href="/financing" className="inline-flex items-center justify-center gap-2 bg-[#FFB600] text-[#002301] px-7 py-3.5 rounded-full font-semibold text-sm hover:bg-white transition-all duration-300">
                Apply for Financing
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 }}
            className="relative rounded-2xl overflow-hidden shadow-2xl"
          >
            <img
              src="/assets/img/financing.png"
              alt="Solar financing for Nigerian homes"
              className="w-full object-cover aspect-[4/3]"
            />
          </motion.div>
        </div>
      </section>

      {/* FINAL CTA - before footer */}
      <section className="bg-[#f4f6f6] py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" className="opacity-5">
            <defs>
              <pattern id="cta-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#002301" strokeWidth="0.8" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#cta-grid)" />
          </svg>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-3xl mx-auto text-center"
        >
          <p className="text-sm font-medium text-[#17BC2E] uppercase tracking-widest mb-3">Join the movement</p>
          <h2 className="text-[#080A09] text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5">
            Ready to take control of your energy future?
          </h2>
          <p className="text-gray-500 text-lg leading-relaxed mb-8">
            Join thousands of homeowners, manufacturers, and distributors already using Ren Power Group.
            Getting started takes less than two minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              href="/auth/register"
              className="inline-flex items-center justify-center gap-2 bg-[#002301] text-white px-8 py-4 rounded-full font-semibold hover:bg-black hover:scale-105 transition-all duration-300"
            >
              Create Free Account
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              href="/marketplace"
              className="inline-flex items-center justify-center gap-2 bg-[#FFB600] text-[#002301] px-8 py-4 rounded-full font-semibold hover:bg-[#17BC2E] hover:text-white transition-all duration-300"
            >
              Browse Marketplace
            </Link>
          </div>
        </motion.div>
      </section>

      {/* TRUST BADGES — moved below CTA */}
      <TrustBadgesSection />

      <Footer />
    </div>
  );
}
